import moment from "moment";
import { observer } from "mobx-react-lite";
import { Loader2Icon, EraserIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useChatState } from "@/components/Playground/Drawer/Chat/hooks/useChatState";
import { BasicTooltip, InfoTooltipContent, InfoTooltipSection } from "@/components/ui/Tooltip";
import { formatLatency } from "@/components/Playground/utils";
import { useChatWidgetContext } from "@/components/Playground/Drawer/Chat/context";

interface ChatHeaderActionsProps {
  onReset: React.MouseEventHandler<HTMLButtonElement> & React.MouseEventHandler<HTMLDivElement>;
  isLoading?: boolean;
}

export const ChatHeaderActions = observer(
  ({ onReset, isLoading = false }: ChatHeaderActionsProps) => {
    const {
      stats: { total_cost, total_input_tokens, total_latency_ms, total_output_tokens },
    } = useChatState();

    const { isContainerSizeSmall } = useChatWidgetContext();

    const totalTokens = total_input_tokens + total_output_tokens;
    const totalLatency = total_latency_ms;
    const totalCost = total_cost;

    const tokenUsageSections: InfoTooltipSection[] = [
      {
        key: "token-usage",
        title: "Token Usage",
        items: [
          { key: "total", label: "Total", value: totalTokens.toLocaleString() },
          { key: "input", label: "Input tokens", value: total_input_tokens.toLocaleString() },
          { key: "output", label: "Output tokens", value: total_output_tokens.toLocaleString() },
          {
            key: "latency",
            label: "Total latency",
            value: formatLatency(moment.duration(totalLatency, "ms")),
          },
        ],
      },
      {
        key: "cost",
        title: "Cost Estimation *",
        items: [{ key: "total-cost", label: "Total cost", value: `$${totalCost.toFixed(5)}` }],
      },
    ];

    const tokenUsageTooltip = (
      <InfoTooltipContent
        sections={tokenUsageSections}
        footer="*: This is an estimated cost if you make the same request via API."
        className="w-64"
      />
    );

    return (
      <div className="flex w-full items-center justify-between gap-2">
        <BasicTooltip content={tokenUsageTooltip} variant="white" side="top" offset={10}>
          <span className="rounded-full bg-gray-50 px-2 py-1 text-xs font-medium text-gray-500">
            {totalTokens.toLocaleString()} tokens
          </span>
        </BasicTooltip>

        <BasicTooltip content="Clear the chat history">
          <Button
            variant="ghost"
            size={isContainerSizeSmall ? "icon" : "sm"}
            onClick={onReset}
            disabled={isLoading}
            className="h-7 rounded-full px-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
            about="Clear the chat history"
          >
            {isLoading ? (
              <>
                <Loader2Icon className="mr-1 h-3 w-3 animate-spin" />
                {!isContainerSizeSmall && <span>Clearing...</span>}
              </>
            ) : (
              <>
                <EraserIcon className="mr-1 h-4 w-4" />
                {!isContainerSizeSmall && <span>Clear</span>}
              </>
            )}
          </Button>
        </BasicTooltip>
      </div>
    );
  },
);
